import { useAtomValue } from 'jotai'
import { memo, useState } from 'react'
import { useIsAiGeneratedImageSrc } from '../ai-generated'
import { externalImageRefAtoms } from '../atoms'
import { useCanvasStore } from '../canvas-store'
import type { Background, BorderRadiusCorners, ImageContentMode, ImageRefTarget, ImageWidth, Spacing } from '../data/v1'
import { borderRadiusStyle } from '../lib/border-radius'
import { opacityStyle } from '../lib/opacity'
import { PlatformLogo } from '../platform-logo'
import { BackgroundFill } from './background-fill'
import { useWidthSetByParentRow } from './substack'

export type ImageLayerRendererProps = {
	layerId: string
	content: ImageContentMode
	refTo: ImageRefTarget
	customValue: string
	width: ImageWidth
	spacing: Spacing
	background: Background
	borderRadius: number
	borderRadiusCorners: BorderRadiusCorners
	opacity: number
}

function resolveWidth(width: ImageWidth): string | undefined {
	switch (width.mode) {
		case 'px':
			return `${width.pxValue}px`
		case '%':
			return `calc(var(--canvas-width) * ${width.percentValue / 100})`
		default:
			return undefined
	}
}

function ImagePlaceholder({ label }: { label: string }) {
	return (
		<div className="flex h-16 w-full items-center justify-center rounded border border-dashed border-red-300 bg-red-50 px-2 text-[11px] text-red-400">
			{label}
		</div>
	)
}

function CustomImage({ src, fill }: { src: string; fill: boolean }) {
	const [errored, setErrored] = useState(false)
	const [lastSrc, setLastSrc] = useState(src)
	const isAiGenerated = useIsAiGeneratedImageSrc(src)

	if (src !== lastSrc) {
		setLastSrc(src)
		setErrored(false)
	}

	if (errored) {
		return <ImagePlaceholder label="Image failed to load" />
	}

	return (
		<img
			src={src}
			alt=""
			onError={() => setErrored(true)}
			data-ai-generated={isAiGenerated ? 'true' : undefined}
			style={{ width: fill ? '100%' : undefined, maxWidth: '100%', display: 'block' }}
		/>
	)
}

function RefLogo({ fill }: { fill: boolean }) {
	const store = useCanvasStore()
	const url = useAtomValue(externalImageRefAtoms.logo, { store })

	if (!url) {
		return <ImagePlaceholder label="{logo}" />
	}

	return <CustomImage src={url} fill={fill} />
}

function RefImage({ refTo, fill }: { refTo: ImageRefTarget; fill: boolean }) {
	if (refTo === 'platformLogo') {
		return <PlatformLogo />
	}

	return <RefLogo fill={fill} />
}

export const ImageLayerRenderer = memo(
	function ImageLayerRenderer(
		{
			layerId,
			content,
			refTo,
			customValue,
			width,
			spacing,
			background,
			borderRadius,
			borderRadiusCorners,
			opacity,
		}: ImageLayerRendererProps,
	) {
		const widthValue = resolveWidth(width)
		const widthSetByParent = useWidthSetByParentRow()
		const hasExplicitSize = width.mode !== 'auto' || widthSetByParent

		const inner = (() => {
			if (content === 'ref') {
				return <RefImage refTo={refTo} fill={hasExplicitSize} />
			}

			if (!customValue) {
				return <ImagePlaceholder label="No image URL" />
			}

			return <CustomImage src={customValue} fill={hasExplicitSize} />
		})()

		return (
			<div
				data-layer-id={layerId}
				className="relative overflow-hidden"
				style={{
					pointerEvents: 'auto',
					boxSizing: 'content-box',
					width: widthValue,
					...borderRadiusStyle(borderRadius, borderRadiusCorners),
					...opacityStyle(opacity),
					paddingTop: spacing.paddingTop,
					paddingBottom: spacing.paddingBottom,
					paddingLeft: spacing.paddingLeft,
					paddingRight: spacing.paddingRight,
					marginTop: spacing.marginTop,
					marginBottom: spacing.marginBottom,
					marginLeft: spacing.marginLeft,
					marginRight: spacing.marginRight,
				}}
			>
				<BackgroundFill background={background} />
				<div className="relative">
					{inner}
				</div>
			</div>
		)
	},
)
